import { format } from "date-fns";
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import ApiService from "../services/apiService";

type UsePaginatedFetchProps = {
  endpoint: string;
  pageSize?: number;
  transform?: (data: any[]) => any[];
  initialSearchQuery?: string;
  filterOn?: string;
  sortBy?: string;
  enabled?: boolean;
  refreshKey?: number;
};

const extractPaginatedItems = (body: any) => {
  if (Array.isArray(body)) return body;
  if (Array.isArray(body?.data)) return body.data;
  if (Array.isArray(body?.data?.data)) return body.data.data;
  return [];
};

const hasServerTotalCount = (body: any) =>
  typeof body?.totalCount === "number" ||
  typeof body?.data?.totalCount === "number" ||
  typeof body?.count === "number";

const extractTotalCount = (body: any, items: any[]) => {
  if (typeof body?.totalCount === "number") return body.totalCount;
  if (typeof body?.data?.totalCount === "number") return body.data.totalCount;
  if (typeof body?.count === "number") return body.count;
  return items.length;
};

export const usePagination = ({
  endpoint,
  pageSize = 10,
  transform,
  initialSearchQuery,
  filterOn,
  sortBy,
  enabled,
  refreshKey,
}: UsePaginatedFetchProps) => {
  const [data, setData] = useState<any[]>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [sort, setSortBy] = useState<string>(sortBy || "");
  const [fromDate, setFromDate] = useState<any>();
  const [toDate, setToDate] = useState<any>();
  const [searchQuery, setSearchQuery] = useState<string>(initialSearchQuery || "");
  const [id, setId] = useState<number | null>(null);
  const [pumpingStationId, setPumpingStationId] = useState<number | string>();
  const [schemeVillageId, setSchemeVillageId] = useState<number | string>();
  const [deviceType, setDeviceType] = useState<string>();
  const [WuaId, setWauId] = useState<string>();
  const [reportType, setReportType] = useState<string>("");
  const [kml, setKml] = useState<number>(1);

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalCount / pageSize)),
    [pageSize, totalCount],
  );

  const canGoToPreviousPage = pageNumber > 1;
  const canGoToNextPage = pageNumber < totalPages;

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await ApiService.axiosApi(endpoint, {
        method: "GET",
        params: {
          pageNumber,
          pageSize,
          filterOn:    filterOn || undefined,
          filterQuery: searchQuery.trim() || undefined,
          sortBy:      sort || undefined,
          isAscending: true,
          fromDateTime: fromDate ? format(fromDate, "yyyy-MM-dd HH:mm:ss") : "",
          toDateTime:   toDate   ? format(toDate,   "yyyy-MM-dd HH:mm:ss") : "",
          instrumentId: id,
          DeviceType:   deviceType,
        },
      });

      if (response.success) {
        const rawItems  = extractPaginatedItems(response.data);
        const items     = transform ? transform(rawItems) : rawItems;

        setData(items);
        setTotalCount(
          hasServerTotalCount(response.data)
            ? extractTotalCount(response.data, items)
            : items.length,
        );
      } else {
        toast.error("Failed to fetch data");
      }
    } catch (err: any) {
      toast.error(err.status || "Fetch error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (enabled) {
      fetchData();
    }
  }, [
    pageNumber,
    endpoint,
    pageSize,
    searchQuery,
    sort,
    enabled,
    refreshKey,
    deviceType,
    kml,
  ]);

  // Reset to page 1 whenever search changes
  useEffect(() => {
    setPageNumber(1);
  }, [searchQuery]);

  // Clamp pageNumber if totalPages shrinks
  useEffect(() => {
    if (pageNumber > totalPages) {
      setPageNumber(totalPages);
    }
  }, [pageNumber, totalPages]);

  const goToPage = useCallback(
    (page: number) => {
      setPageNumber(Math.min(Math.max(1, page), totalPages));
    },
    [totalPages],
  );

  const goToNextPage = useCallback(() => {
    setPageNumber((prev) => Math.min(totalPages, prev + 1));
  }, [totalPages]);

  const goToPreviousPage = useCallback(() => {
    setPageNumber((prev) => Math.max(1, prev - 1));
  }, []);

  const resetPagination = useCallback(() => {
    setPageNumber(1);
  }, []);

  return {
    data,
    pageNumber,
    setPageNumber,
    pageSize,
    totalCount,
    totalPages,
    canGoToNextPage,
    canGoToPreviousPage,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    resetPagination,
    loading,
    searchQuery,
    setSearchQuery,
    filterOn,
    setSortBy,
    setFromDate,
    setToDate,
    setId,
    id,
    fromDate,
    toDate,
    setPumpingStationId,
    setData,
    setSchemeVillageId,
    setDeviceType,
    deviceType,
    setWauId,
    setReportType,
    setKml,
    pumpingStationId,
    schemeVillageId,
    WuaId,
    reportType,
    sort,
  };
};